<template>
  <div v-if="showAside" class="text-17 text-app-color bg-primary flex flex-col justify-between h-full w-full overflow-y-auto">
    <div class="w-full pt-30 pb-50 max-w-320 mx-auto">
      <h2 class="mb-30 font-bold text-24">Settings</h2>

      <div v-if="isTeamAccount && logo">
        <img :key="logo" class="w-100 mb-15" :src="logo" alt="logo">
      </div>

      <!-- Account -->
      <div>
        <p class="font-bold mb-5 text-18">Account</p>
        <div
          class="menu-item hover:bg-hover rounded-10 px-20"
          :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'"
          @click="update('Account')"
        >
          <p>General</p>
          <i class="text-14 icon-keyboard-arrow-left transform rotate-180 opacity-50" />
        </div>
      </div>

      <div v-if="isTeamAccount" class="mt-40">
        <p class="font-bold mb-5 text-18">Team</p>
        <div
          class="menu-item hover:bg-hover rounded-10 px-20"
          :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'"
          @click="update('TeamMembers')"
        >
          <p>Members</p>
          <i class="text-14 icon-keyboard-arrow-left transform rotate-180 opacity-50" />
        </div>
      </div>

      <!-- Design -->
      <div class="mt-40">
        <p class="font-bold mb-5 text-18">Design</p>
        <div class="rounded-10" :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'">
          <!-- Font family -->
          <div class="hover:bg-hover px-20">
            <div class="menu-item border-b border-hover" @click="scrollTo('font-family')">
              <p>Font family</p>
              <div class="flex items-center text-14 opacity-50">
                <p>{{ settings.fontFamily }}</p>
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>

          <!-- Font size -->
          <div class="hover:bg-hover px-20">
            <div class="menu-item border-b border-hover" @click="scrollTo('font-size')">
              <p>Font size</p>
              <div class="flex items-center text-14 opacity-50">
                <p>{{ settings.fontSize }}</p>
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>

          <!-- Theme -->
          <div class="hover:bg-hover px-20 rounded-b-10" @click="scrollTo('theme')">
            <div class="menu-item border-b border-hover">
              <p>Theme</p>
              <div class="flex items-center text-14 opacity-50">
                <p>{{ settings.theme }}</p>
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>

          <!-- mode -->
          <div class="menu-item hover:bg-hover px-20" @click="scrollTo('document-mode')">
            <p>Paper mode</p>
            <div class="flex items-center text-14 opacity-50">
              <p>{{ settings.mode }}</p>
              <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
            </div>
          </div>
        </div>
      </div>

      <!-- Preferences -->
      <div class="mt-40">
        <p class="font-bold mb-5 text-18">Preferences</p>
        <div class="rounded-10" :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'">
          <!-- Checkboxes -->
          <div class="px-20 rounded-t-10">
            <div class="menu-item" @click="update('Preferences')">
              <p>Preferences</p>
              <div class="flex items-center text-14 opacity-50">
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Help -->
      <div class="mt-40">
        <p class="font-bold mb-5 text-18">Help</p>
        <div class="rounded-10" :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'">
          <!-- Keyboard -->
          <div class="hover:bg-hover px-20 rounded-t-10">
            <div class="menu-item border-b border-hover" @click="update('Keyboard')">
              <p>Keyboard shortcuts</p>
              <div class="flex items-center text-14 opacity-50">
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>

          <!-- Send -->
          <a href="mailto:hello@helloivy.co" target="_blank" class="menu-item hover:bg-hover rounded-b-10 px-20">
            <p>Send us a message</p>
            <span class="flex items-center text-14 opacity-50">
              <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
            </span>
          </a>
        </div>
      </div>

      <!-- Payment & plans -->
      <div v-if="showPlan" class="mt-40">
        <p class="font-bold mb-5 text-18">Payment & plans</p>
        <div class="rounded-10" :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'">
          <!-- Billing -->
          <div class="hover:bg-hover px-20 rounded-t-10">
            <div class="menu-item border-b border-hover" @click="update('Billing')">
              <p>Billing</p>
              <div class="flex items-center text-14 opacity-50">
                <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
              </div>
            </div>
          </div>

          <!-- Plans -->
          <div class="menu-item hover:bg-hover rounded-b-10 px-20" @click="update('Plans')">
            <p>Your Plan</p>
            <div class="flex items-center text-14 opacity-50">
              <i class="icon-keyboard-arrow-left transform rotate-180 ml-16" />
            </div>
          </div>
        </div>
      </div>

      <!-- Restore -->
      <div v-if="showTrash" class="mt-40">
        <p class="font-bold mb-5 text-18">Restore</p>
        <div
          class="menu-item hover:bg-hover rounded-10 px-20"
          :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'"
          @click="update('Trash')"
        >
          <p>Trash</p>
          <i class="text-14 icon-keyboard-arrow-left transform rotate-180 opacity-50" />
        </div>
      </div>

      <!-- Get most out of Hello Ivy -->
      <div v-if="isFreePlan && !isTeamAccount" class="mt-40">
        <p class="font-bold mb-5 text-18">Get most out of Hello Ivy</p>
        <div
          class="menu-item hover:bg-hover rounded-10 px-20"
          :class="isDarkMode ? 'bg-sidebar' : 'bg-grey-450'"
          @click="upgradeToPro"
        >
          <div class="flex items-center">
            <img :src="require(`@/assets/images/thunder.svg`)" alt="" class="w-16 h-16 mr-10">
            <p class="text-blue-main font-semibold">Upgrade to pro</p>
          </div>
          <i class="text-14 icon-keyboard-arrow-left transform rotate-180 opacity-50" />
        </div>
      </div>

      <ProgressBarModule v-if="isFreePlan || (isTrialPlan && isOwner)" />

      <!-- L O G  O U T  B U T T O N -->
      <ButtonModule
        text="Log out"
        class="bg-link-color text-white text-16 rounded-4 py-12 px-40 mt-30 mb-50 w-full flex flex-col items-center"
        @click="logout"
      />
    </div>
  </div>
</template>

<script>
import ProgressBarModule from '@/components/shared/ProgressBarModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'

import { mapGetters, mapMutations, mapState } from 'vuex'

import localStorageService from '@/core/local-storage'

export default {

  components: {
    ProgressBarModule,
    ButtonModule
  },

  emits: ['toggle'],

  computed: {
    ...mapState({
      settings: state => state.settings.settings,
      showAside: state => state.settings.showAside,
      activeState: state => state.settings.activeState,
      user: state => state.profile.user,
      team: state => state.team.team
    }),

    ...mapGetters(['isFreePlan', 'isDarkMode', 'isAdmin', 'isOwner', 'isTeamAccount', 'isTrialPlan']),

    showTrash () {
      return this.isTeamAccount ? this.isAdmin : true
    },

    showPlan () {
      return this.isTeamAccount ? this.isOwner : true
    },

    logo () {
      return this.team?.teamLogo?.url
    }
  },

  methods: {
    ...mapMutations(['changeActiveState', 'toggleSettingsModal', 'toggleAside']),

    update (value) {
      this.changeActiveState(value)
      this.$emit('toggle')
    },

    scrollTo (to) {
      this.update('Design')

      setTimeout(() => {
        const el = document.getElementById(to)
        if (el) el.scrollIntoView(true)
      }, 100)
    },

    upgradeToPro () {
      this.toggleAside(false)
      this.changeActiveState('Plans')
    },

    logout () {
      this.$router.push({ name: 'Login' })
      this.toggleSettingsModal(false)
      localStorageService.removeItem('lastOpenedProject')
    }
  }
}
</script>

<style lang="scss">
.menu-item {
  @apply flex items-center w-full justify-between py-15 cursor-pointer;
}
</style>
